import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

const Backups: React.FC = () => {
  return (
    <>
      <Helmet>
        <title>Backups — Sprout Docs</title>
        <meta
          name="description"
          content="Sprout's database backup policy: where backups live, retention windows, encryption, and the limits we are honest about."
        />
        <link rel="canonical" href="https://sprout-ui-pi.vercel.app/docs/security/backups" />
      </Helmet>

      <h1>Backups</h1>
      <p className="lead">
        POPIA section 19 obliges the responsible party to take reasonable
        technical and organisational measures to prevent loss of personal
        information. Backups are part of that. This page documents what
        Sprout actually does today — including the bits that are still on
        the roadmap.
      </p>

      <h2>Where backups live</h2>
      <p>
        Sprout's production database is managed PostgreSQL on Supabase.
        Backups are taken and stored by Supabase, in the same region as
        the primary database (EU-Central-1, Frankfurt). They never leave
        that region as part of normal operation, so the backup pathway
        does not introduce a separate cross-border transfer beyond the
        primary-database transfer already covered in{' '}
        <Link to="/docs/security/data-residency">Data Residency</Link>.
      </p>

      <h2>Cadence and retention</h2>
      <p>
        Supabase's current default for paid projects is daily snapshots,
        with point-in-time recovery available on the Pro tier. Snapshot
        retention on Pro is seven days by default. <strong>We have not
        extended the default retention window.</strong> If your school or
        district has a longer retention requirement, get in touch and we
        can discuss raising it under a custom mandate.
      </p>
      <p>
        These numbers reflect the Supabase platform's current defaults,
        which can change over time. The authoritative source is the
        Supabase project console; this page is a plain-language summary
        for schools who want to know what's happening on their behalf.
      </p>

      <h2>Encryption</h2>
      <p>
        Backups are encrypted at rest with AES-256 by the Supabase
        storage layer. Restores transit the same TLS 1.2+ channel used
        for normal database operations — no plaintext on the wire,
        no plaintext on disk.
      </p>

      <h2>Test restores</h2>
      <p>
        We are honest about this: <strong>we have not yet performed a
        scheduled test-restore drill</strong>. The platform supports
        point-in-time recovery and we have validated the mechanism
        manually, but there is no recurring drill on a calendar. Adding
        one is on the roadmap.
      </p>
      <p>
        For schools that need a documented restore-test cadence as part
        of their own POPIA evidence trail, this is something we will
        prioritise — please flag it in your onboarding.
      </p>

      <h2>What backups do not reach</h2>
      <ul>
        <li>
          <strong>Application logs.</strong> Logs are emitted to the
          hosting provider's stdout pipeline and are not part of the
          database backup set. They contain no personal information by
          design (see <Link to="/docs/security/popia">POPIA</Link>).
        </li>
        <li>
          <strong>Object-storage media.</strong> Photos and short videos
          attached to observations live in S3-compatible object storage,
          versioned at the bucket level. They are not part of the
          PostgreSQL backup window — versioning is the recovery
          mechanism for media.
        </li>
        <li>
          <strong>Deleted records, after the retention window expires.</strong>{' '}
          A child record deleted from the platform survives in backup
          archives until the seven-day rolling window expires; after
          that, it's gone everywhere. This is the practical retention
          floor described in our <Link to="/docs/security/popia">POPIA</Link>{' '}
          page.
        </li>
      </ul>

      <h2>Related</h2>
      <p>
        See <Link to="/docs/security/popia">POPIA Compliance</Link> for
        the broader security-safeguard story under section 19, and{' '}
        <Link to="/docs/security/data-residency">Data Residency</Link>{' '}
        for where the primary database (and therefore the backup set)
        physically lives.
      </p>
    </>
  );
};

export default Backups;
